import { useEffect, useState } from 'react';

const PING_INTERVAL_MS = 30000;
const FAILURE_THRESHOLD = 3;

const listeners = new Set();
let consecutiveFailures = 0;
let state = {
  isOnline: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  lastSyncAt: null,
};
let pingTimer = null;

const emit = () => listeners.forEach((listener) => listener(state));

const setState = (next) => {
  state = { ...state, ...next };
  emit();
};

const ping = async () => {
  if (typeof fetch === 'undefined') return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    consecutiveFailures = FAILURE_THRESHOLD;
    setState({ isOnline: false });
    return;
  }

  try {
    const response = await fetch('/manifest.json', {
      method: 'HEAD',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Ping failed: ${response.status}`);
    consecutiveFailures = 0;
    setState({ isOnline: true, lastSyncAt: new Date() });
  } catch {
    consecutiveFailures += 1;
    if (consecutiveFailures >= FAILURE_THRESHOLD) {
      setState({ isOnline: false });
    }
  }
};

const startPingLoop = () => {
  if (pingTimer || typeof window === 'undefined') return;
  pingTimer = window.setInterval(() => {
    if (navigator.onLine !== false || !state.isOnline) ping();
  }, PING_INTERVAL_MS);
};

export const reportMutationFailure = () => {
  consecutiveFailures += 1;
  if (consecutiveFailures >= FAILURE_THRESHOLD || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    setState({ isOnline: false });
  }
  startPingLoop();
};

export const markMutationSuccess = () => {
  consecutiveFailures = 0;
  setState({ isOnline: true, lastSyncAt: new Date() });
};

export const checkConnectivity = ping;

export const subscribeConnectivity = (listener) => {
  listeners.add(listener);
  listener(state);
  startPingLoop();
  return () => listeners.delete(listener);
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    consecutiveFailures = 0;
    setState({ isOnline: true, lastSyncAt: new Date() });
    ping();
  });
  window.addEventListener('offline', () => {
    consecutiveFailures = FAILURE_THRESHOLD;
    setState({ isOnline: false });
  });
  startPingLoop();
}

export const useConnectivity = () => {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => subscribeConnectivity(setSnapshot), []);
  return snapshot;
};

export default useConnectivity;
